import { apiPost } from "./api";

export interface StoredFile {
  storagePath: string;
  originalName: string;
  size: number;
  mime: string;
}

/** Upload a file straight to Backblaze B2 via a server-issued presigned PUT, reporting 0..100. */
export async function uploadToStorage(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<StoredFile> {
  const { storagePath, uploadUrl } = await apiPost<{ storagePath: string; uploadUrl: string }>(
    "/uploads/presign",
    { contentType: file.type || "application/octet-stream" },
  );
  await putWithProgress(uploadUrl, file, onProgress);
  return { storagePath, originalName: file.name, size: file.size, mime: file.type };
}

function putWithProgress(
  url: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`업로드 실패 (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("업로드 중 네트워크 오류가 발생했습니다"));
    xhr.send(file);
  });
}

/** Resolve a temporary download URL for a stored file path. */
export async function getFileDownloadURL(storagePath: string): Promise<string> {
  const { url } = await apiPost<{ url: string }>("/files/download-url", { storagePath });
  return url;
}

/** Resolve an INLINE temporary URL (renderable in <img> / fetchable as text) for a preview. */
export async function getFileViewURL(storagePath: string): Promise<string> {
  const { url } = await apiPost<{ url: string }>("/files/view-url", { storagePath });
  return url;
}

/**
 * Download a stored file to disk under its ORIGINAL name + extension. We fetch the
 * object as a blob and trigger an `<a download>` so the browser saves it with the
 * exact filename (a cross-origin URL alone would save it under the opaque storage key).
 */
export async function downloadFile(storagePath: string, fileName: string): Promise<void> {
  const url = await getFileDownloadURL(storagePath);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`다운로드 실패 (HTTP ${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
